// Feature-discovery tip catalogue. Surfaced one-at-a-time as a small
// card at the top of the home page, so users learn about the
// otherwise-hidden modules ("there's a couples quiz!", "you can
// generate a PDF report") without having to dig through the nav.
//
// Each entry:
//   id        stable string; tracked in localStorage so a seen tip
//             doesn't re-pop next session. Pick something descriptive
//             once and never rename — that breaks "already seen" state.
//   icon      lucide-react icon NAME (string, not the component). The
//             TipCard renders it by looking up the icon-name → component
//             map locally so the lib/tips file stays UI-free.
//   title     { en, ar } — short headline (≤ ~25 chars each)
//   body      { en, ar } — 1-line explanation
//   cta       { en, ar } — button label for the deep-link
//   route     where the "Try it" CTA navigates to
//
// Add a new tip by appending an entry. Order = priority — the next
// unseen tip from the top of the array is what surfaces.
export const TIPS = [
  {
    id: 'couples-quiz',
    icon: 'Sparkles',
    title: { en: 'Couples Quiz', ar: 'كويز للاتنين' },
    body: {
      en: 'See how well you know each other — 5 questions a round, you both guess each other\'s answers.',
      ar: 'شوف بتعرف شريكك قد إيه — ٥ أسئلة كل جولة، الاتنين بتخمّنوا إجابات بعض.',
    },
    cta: { en: 'Try it', ar: 'جرّبها' },
    route: '/games/quiz',
  },
  {
    id: 'settings',
    icon: 'Settings',
    title: { en: 'Make it yours', ar: 'خصّص التطبيق على ذوقك' },
    body: {
      en: 'Change theme, accent colour, font, voices, and tune notifications — all from the Settings page.',
      ar: 'من الإعدادات تقدر تغيّر الثيم، اللون، الخط، الأصوات، وتظبّط الإشعارات على راحتك.',
    },
    cta: { en: 'Open Settings', ar: 'افتح الإعدادات' },
    route: '/settings',
  },
  {
    id: 'daily-question',
    icon: 'HelpCircle',
    title: { en: 'Question of the Day', ar: 'سؤال اليوم' },
    body: {
      en: 'One bilingual question a day. Both of you answer, then the reveal shows your picks side-by-side.',
      ar: 'سؤال جديد كل يوم. الاتنين بتجاوبوا، وبعدين بتشوفوا اختياراتكم جنب بعض.',
    },
    cta: { en: 'Try it', ar: 'جرّبها' },
    route: '/games/questions',
  },
  {
    id: 'wordle',
    icon: 'Shuffle',
    title: { en: 'Daily Wordle', ar: 'وردل يومي' },
    body: {
      en: 'Five-letter word, six guesses. Streak tracks how many days in a row you both played.',
      ar: 'كلمة ٥ حروف، ٦ محاولات. السلسلة بتعد الأيام اللي لعبتوا فيها سوا.',
    },
    cta: { en: 'Play', ar: 'العب' },
    route: '/games/wordle',
  },
  {
    id: 'sports',
    icon: 'TrendingUp',
    title: { en: 'Football Fixtures', ar: 'جدول الكورة' },
    body: {
      en: 'Live scores, standings, and your followed teams\' upcoming matches — all in one place.',
      ar: 'النتايج اللحظية، الترتيب، ومواعيد ماتشات فرقك المفضلة — كل ده في مكان واحد.',
    },
    cta: { en: 'See', ar: 'شوف' },
    route: '/sports',
  },
  {
    id: 'reports',
    icon: 'BookOpen',
    title: { en: 'Period Reports', ar: 'تقارير الفترة' },
    body: {
      en: 'Generate an HTML report covering your weight, meals, workouts, and progress for any date range.',
      ar: 'اعمل تقرير HTML بيغطي وزنك، أكلك، تمارينك، وتقدّمك في أي فترة تختارها.',
    },
    cta: { en: 'Generate', ar: 'اعمل تقرير' },
    route: '/reports',
  },
  {
    id: 'finance-dues',
    icon: 'Bell',
    title: { en: 'Finance Dues Sheet', ar: 'مواعيد دفعاتك' },
    body: {
      en: 'Track upcoming installments, recurring bills, and obligations — never miss a due date.',
      ar: 'تابع الأقساط، الفواتير الشهرية، والالتزامات — مش هتفوّت أي تاريخ.',
    },
    cta: { en: 'See dues', ar: 'شوف المواعيد' },
    route: '/finance',
  },
  {
    id: 'phases',
    icon: 'PartyPopper',
    title: { en: 'Life Phases', ar: 'مراحل الحياة' },
    body: {
      en: 'Map the chapters of your life — bulking, recovery, immigration, study. Each phase gets its own page.',
      ar: 'حدّد فصول حياتك — تضخيم، تعافي، هجرة، دراسة. كل مرحلة ليها صفحتها.',
    },
    cta: { en: 'Open', ar: 'افتح' },
    route: '/phases',
  },
  {
    id: 'suggest-question',
    icon: 'ChevronRight',
    title: { en: 'Suggest a Question', ar: 'اقترح سؤال' },
    body: {
      en: 'Got a question you\'d love to see on the Quiz? Drop it from /games/questions — it joins the bank instantly.',
      ar: 'عندك سؤال تحب تشوفه في الكويز؟ ضيفه من صفحة الأسئلة وهيدخل في المجموعة على طول.',
    },
    cta: { en: 'Suggest', ar: 'اقترح' },
    route: '/games/questions',
  },
]

// Tips are non-dismissable — the home card rotates through the full
// catalogue forever so users keep seeing feature pointers every time
// they open the app. No storage, no per-user state.
